import React from 'react'
import PropTypes from 'prop-types'
import { toast, ToastContainer } from 'react-toastify'

import { NewArtwork } from '@domain-group/artwork-builder'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes } from '@fortawesome/pro-light-svg-icons'

import useArtworkBuilder from '@hooks/useArtworkBuilder'
import useOrderItem from '@hooks/useOrderItem'
import { buildEditArtworkUrl } from '@hooks/useArtworkBuilderPaths'

import { useEffect } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { setCookie, getCookie} from '@functions/cookie'

import 'react-toastify/dist/ReactToastify.css'
import '@domain-group/artwork-builder/dist/styles.css'
import '@styles/toast.scss'

const CloseButton = ({ closeToast }) => (
  <a onClick={closeToast}>
    <FontAwesomeIcon icon={faTimes} />
  </a>
)

const goBack = (campaign, redirect) => {
  if (redirect){
    window.location.href = redirect
    return
  }

  window.location.hash = `/campaigns/${campaign.id}/edit`
}

const linkArtworkToOrderItem = (campaign, artwork, linkFn, redirect) => {
  linkFn(artwork.id).then((response) => {
    const { success, errors = [] } = response

    if (!success){
      toast.warn(errors[0])
      return
    }

    useEffect(() => {
      window.newrelic?.setCustomAttribute("Realhub Artwork ID", artwork.id);
    }, [artwork.id]);

    const editArtworkUrl = buildEditArtworkUrl(artwork, campaign, { redirectUrl: redirect || null })
    window.location.href = editArtworkUrl
  })
}

const selectSavedArtwork = async (campaign, savedArtwork, createFn, loadShortcodeDataFn, linkFn, redirect) => {
  const shortcodeDataResult = await loadShortcodeDataFn()
  const { data: customShortcodeData } = shortcodeDataResult

  const options = {
    custom_data: customShortcodeData,
    create_attached_item_if_missing: true,
  }

  createFn(savedArtwork, options).then((response) => {
    const { success, data: artwork, errors = [] } = response
    if (!success){
      toast.warn(errors[0])
      return
    }

    // Link the Artwork to CT Order Item
    linkArtworkToOrderItem(campaign, artwork, linkFn, redirect)
  })
}

const selectTemplatePage = async (campaign, templatePage, createFn, loadShortcodeDataFn, linkFn, options, redirect) => {
  const shortcodeDataResult = await loadShortcodeDataFn()
  const { data: customShortcodeData } = shortcodeDataResult

  const mergedOptions = {
    ...options,
    create_attached_item_if_missing: true,
    custom_data: customShortcodeData,
  }

  createFn(templatePage, mergedOptions).then((response) => {
    const { success, data: artwork, errors = [] } = response
    if (!success){
      toast.warn(errors[0])
      return
    }

    // Link the Artwork to CT Order Item
    linkArtworkToOrderItem(campaign, artwork, linkFn, redirect)
  })

  useEffect(() => {
    window.newrelic?.setCustomAttribute("Realhub Template", templatePage);
  }, [templatePage]);
}

const NewPage = (props) => {
  const { children } = props

  const {
    callbacks: { fetchNewArtworkOptions },
    campaign,
    orderItemId,
    redirect,
  } = useArtworkBuilder()

  const {
    callbacks: {
      linkArtworkToOrderItem: linkFn,
      loadShortcodeData: loadShortcodeDataFn,
    },
  } = useOrderItem({ id: orderItemId })

  const { user } = useAuth0()

  const currentEmail = user.email 
  const currentUserSub = user.sub

  const lastEmail = getCookie('lastEmail')
  const lastUserSub = getCookie('lastUserSub')

  const lastAuthenticatedUser = getCookie('lastAuthenticatedUser')
  const loginAttemptUser = getCookie('authenticatedUser')

  setCookie('currentEmail', currentEmail, 86400)
  setCookie('currentUserSub', currentUserSub, 86400)

  useEffect(() => {
    window.newrelic?.setCustomAttribute("Realhub Order Item ID", orderItemId)

    if (lastUserSub === currentUserSub){
      const error = new Error('Auth0 stale session reuse')
      
    window.newrelic?.noticeError(error, {
        lastEmail,
        currentEmail,
        lastUserSub,
        currentUserSub,
        lastAuthenticatedUser,
        loginAttemptUser,
        type: 'AUTH0_SESSION_REUSE',
      })
    }
  }, [orderItemId]);

  return (
    <NewArtwork
      callbacks={{
        artworkUploadSuccess: () => goBack(campaign, redirect),
        closeSelectTemplateModal: () => goBack(campaign, redirect),
        fetchEditorOptions: fetchNewArtworkOptions,
        selectSavedArtwork: (savedArtwork, createFn) => selectSavedArtwork(campaign, savedArtwork, createFn, loadShortcodeDataFn, linkFn, redirect),
        selectTemplatePage: (templatePage, createFn, _, options) => selectTemplatePage(campaign, templatePage, createFn, loadShortcodeDataFn, linkFn, options, redirect),
      }}
    >
      {children}

      <ToastContainer closeButton={<CloseButton />} hideProgressBar autoClose={5000} />
    </NewArtwork>
  )
}

NewPage.propTypes = {
  children: PropTypes.node,
}

export default NewPage
